import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, Subscription, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../environments/environment';
import { WebSocketService } from './websockets.service';

type NotificationType = 'PASSAGE_REQUEST' | 'UNRECOGNIZED_PLATE';
interface Notification {
  type: string;
  message: string;
  timestamp: string;
  data: any;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private notificationSubject = new Subject<Notification>();
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();
  private isListening = false;

  private reloadTrigger = new BehaviorSubject<void>(undefined);
  reloadTrigger$ = this.reloadTrigger.asObservable();
  refreshTrigger = new Subject<void>();

  get notifications$(): Observable<Notification> {
    return this.notificationSubject.asObservable();
  }

  get refreshTrigger$(): Observable<void> {
    return this.refreshTrigger.asObservable();
  }

  private handleNotification(data: any): void {
    try {
      const notification = typeof data === 'string' ? JSON.parse(data) : data;
      if (notification.type !== 'LIVE_MESSAGE') {
        return;
      }
      this.notificationSubject.next(notification);
      this.refreshTrigger.next();
      this.snackBar.open(notification.message, 'Close', { duration: 5000, panelClass: ['notification-snackbar'] });
    } catch (error) {
      console.error('Error processing notification:', error, data);
    }
  }

  get connectionStatus$(): Observable<boolean> {
    return this.connectionStatusSubject.asObservable();
  }

  constructor(
    private webSocketService: WebSocketService,
    private snackBar: MatSnackBar
  ) { }

  async startListening(): Promise<void> {
    if (this.isListening) return;

    await this.webSocketService.connect('notifications', `${environment.wsBase}/notifications`);

    this.webSocketService.getConnectionStatus('notifications')
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.connectionStatusSubject.next(status);
        if (!status) {
          this.snackBar.open('Disconnected from notifications', 'Close', { duration: 3000 });
        }
      });

    this.webSocketService.onMessage('notifications')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => this.handleNotification(data),
        error: err => {
          console.error('WebSocket notification error:', err);
          this.snackBar.open('Notification error', 'Close', { duration: 5000 });
        }
      });

    this.isListening = true;
  }

  stopListening(): void {
    this.destroy$.next();
    this.webSocketService.disconnect('notifications');
    this.isListening = false;
  }

  ngOnDestroy(): void {
    this.stopListening();
    this.destroy$.complete();
    this.notificationSubject.complete();
    this.connectionStatusSubject.complete();
  }
}